import useSWR from "swr";

import * as fetcher from "../fetcher";

interface UserDetailsResult {
  displayName?: string;
  givenName?: string;
  surname?: string;
  mail?: string;
}

interface PendingEntry {
  email: string;
  resolve: (value: UserDetailsResult | undefined) => void;
  reject: (reason: unknown) => void;
}

let pendingEntries: PendingEntry[] = [];
let batchScheduled = false;

const flushBatch = () => {
  const batch = pendingEntries;
  pendingEntries = [];
  batchScheduled = false;

  if (batch.length === 0) {
    return;
  }

  const emailByLower = new Map<string, string>();
  batch.forEach(({ email }) => {
    const lower = email.toLowerCase();
    if (!emailByLower.has(lower)) {
      emailByLower.set(lower, email);
    }
  });
  const uniqueEmails = Array.from(emailByLower.values());

  fetcher.coworker
    .post<UserDetailsResult[], false>("/graph/users", {
      coworkerEmailIds: uniqueEmails,
      select: "displayName,surname,givenName,mail",
    })
    .then((res) => {
      const users = Array.isArray(res) ? res : [];
      const byMail = new Map<string, UserDetailsResult>();
      users.forEach((user) => {
        if (user.mail) {
          byMail.set(user.mail.toLowerCase(), user);
        }
      });

      const positionalByEmail =
        users.length === uniqueEmails.length
          ? new Map(
              uniqueEmails.map((email, index) => [
                email.toLowerCase(),
                users[index],
              ])
            )
          : undefined;

      batch.forEach(({ email, resolve }) => {
        const key = email.toLowerCase();
        const value = byMail.get(key) ?? positionalByEmail?.get(key);
        resolve(value);
      });
    })
    .catch((error) => {
      batch.forEach(({ reject }) => reject(error));
    });
};

const enqueue = (email: string): Promise<UserDetailsResult | undefined> =>
  new Promise((resolve, reject) => {
    pendingEntries.push({ email, resolve, reject });
    if (!batchScheduled) {
      batchScheduled = true;
      setTimeout(flushBatch, 0);
    }
  });

export const useUserDetails = (userId?: string) => {
  return useSWR<UserDetailsResult | undefined>(
    userId
      ? `/graph/users?mail=${userId}&select=displayName,surname,givenName`
      : null,
    () => enqueue(userId!),
    { shouldRetryOnError: false, revalidateOnFocus: false }
  );
};
